import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, Toolbar } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navigation from './components/Navigation';
import Dashboard from './components/DashboardFinal';
import FinancialDetails from './components/FinancialDetails';
import Simulation from './components/Simulation';
import DataManagement from './components/DataManagement';
import Settings from './components/Settings';
import UserProfile from './components/UserProfile';
import Login from './components/LoginSimplified';
import { OrganizationRegistration } from './components/OrganizationRegistration';
import AdminInvitationManagement from './components/AdminInvitationManagement';
import PublicAccessManagement from './components/PublicAccessManagement';
import AcceptInvitation from './components/AcceptInvitation';
import PublicDashboard from './components/PublicDashboard';
import { LandingPage } from './components/LandingPage';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { TermsOfService } from './components/TermsOfService';
import { EmailConfirmationCallback } from './pages/EmailConfirmationCallback';

// React Query クライアント設定
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5分間はfreshとみなす
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      'Segoe UI',
      'Roboto',
      'Oxygen',
      'Ubuntu',
      'Cantarell',
      'Fira Sans',
      'Droid Sans',
      'Helvetica Neue',
      'sans-serif',
    ].join(','),
  },
});

const drawerWidth = 240;


// ProtectedRoute コンポーネント（認証が必要なルート用）
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>読み込み中...</Box>;
  }

  if (!user) {
    // ログイン後のリダイレクト先を保存
    sessionStorage.setItem('redirectAfterLogin', location.pathname);
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};


// AuthenticatedLayout コンポーネント（認証済みユーザー用レイアウト）
const AuthenticatedLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // 現在のパスからページを判定
  const getCurrentPage = () => {
    const path = location.pathname;
    if (path === '/dashboard' || path === '/') return 'dashboard';
    if (path === '/financial-details') return 'financial-details';
    if (path === '/simulation') return 'simulation';
    if (path === '/data-management') return 'data-management';
    if (path === '/settings') return 'settings';
    if (path === '/admin-invitations') return 'admin-invitations';
    if (path === '/public-access') return 'public-access';
    return 'dashboard';
  };

  const handlePageChange = (page: string) => {
    const routes: { [key: string]: string } = {
      'dashboard': '/dashboard',
      'financial-details': '/financial-details',
      'simulation': '/simulation',
      'data-management': '/data-management',
      'settings': '/settings',
      'profile': '/profile',
      'admin-invitations': '/admin-invitations',
      'public-access': '/public-access',
    };
    
    if (routes[page]) {
      navigate(routes[page]);
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Navigation currentPage={getCurrentPage()} onPageChange={handlePageChange} />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

// ログインページコンポーネント
const LoginPage: React.FC = () => {
  const [showRegistration, setShowRegistration] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // URLパラメータをチェックして新規登録画面を表示
  React.useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('mode') === 'register') {
      setShowRegistration(true);
    }
  }, [location]);

  const handleLogin = async (email: string, password: string) => {
    try {
      await signIn(email, password);
      
      // ログイン後のリダイレクト処理
      const redirectPath = sessionStorage.getItem('redirectAfterLogin');
      sessionStorage.removeItem('redirectAfterLogin');
      navigate(redirectPath || '/dashboard', { replace: true });
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  if (showRegistration) {
    return (
      <OrganizationRegistration 
        onBack={() => setShowRegistration(false)}
      />
    );
  }

  return (
    <Login 
      onLogin={handleLogin} 
      onShowRegistration={() => setShowRegistration(true)}
    />
  );
};

const AppContent: React.FC = () => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // メール確認のハッシュフラグメントを処理
  React.useEffect(() => {
    if (location.hash && location.hash.includes('access_token')) {
      // ハッシュフラグメントがある場合は、/auth/confirmにリダイレクト
      window.location.href = '/auth/confirm' + location.hash;
    }
  }, [location]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>読み込み中...</Box>;
  }

  return (
    <Routes>
      {/* パブリックルート（認証不要） */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
      <Route path="/auth/confirm" element={<EmailConfirmationCallback />} />
      
      {/* プロテクトされたルート（認証必要） */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <AuthenticatedLayout>
            <Dashboard />
          </AuthenticatedLayout>
        </ProtectedRoute>
      } />
      <Route path="/financial-details" element={
        <ProtectedRoute>
          <AuthenticatedLayout>
            <FinancialDetails />
          </AuthenticatedLayout>
        </ProtectedRoute>
      } />
      <Route path="/simulation" element={
        <ProtectedRoute>
          <AuthenticatedLayout>
            <Simulation />
          </AuthenticatedLayout>
        </ProtectedRoute>
      } />
      <Route path="/data-management" element={
        <ProtectedRoute>
          <AuthenticatedLayout>
            <DataManagement />
          </AuthenticatedLayout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <AuthenticatedLayout>
            <Settings />
          </AuthenticatedLayout>
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <AuthenticatedLayout>
            <UserProfile />
          </AuthenticatedLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin-invitations" element={
        <ProtectedRoute>
          <AuthenticatedLayout>
            <AdminInvitationManagement />
          </AuthenticatedLayout>
        </ProtectedRoute>
      } />
      <Route path="/public-access" element={
        <ProtectedRoute>
          <AuthenticatedLayout>
            <PublicAccessManagement />
          </AuthenticatedLayout>
        </ProtectedRoute>
      } />
      
      {/* 404 - 未定義のルートはルートパスにリダイレクト（適切な振り分けが行われる） */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Routes>
            {/* 公開ルート（認証不要） - AuthProviderの外に配置 */}
            <Route path="/public/:token" element={<PublicDashboard />} />
            
            {/* その他のルート - AuthProviderでラップ */}
            <Route path="/*" element={
              <AuthProvider>
                <AppContent />
              </AuthProvider>
            } />
          </Routes>
        </Router>
        <ReactQueryDevtools initialIsOpen={false} />
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
